import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { AuthAppContainer } from "components/AppContainer/AuthAppContainer";
import { DmsContainer } from "components/AppContainer/DmsAppContainer";
import { DummyContainer } from "components/AppContainer/DummyContainer";
import { PmgAppContainer } from "components/AppContainer/PmgAppContainer";
import { TmsAppContainer } from "components/AppContainer/TmsAppContainer";
import { TmsPmgAppContainer } from "components/AppContainer/TmsPmgAppContainer";
import { AppRouteMap, DefaultApp, getActiveApps, SupportedApp, SupportedApps } from "config/config";
import { Palette } from "config/palette";
import { useApp } from "hooks/useApp";
import { useAuth } from "hooks/useAuth";
import { AppReset } from "pages/AppReset";
import { AuthPage } from "pages/Auth/AuthPage";
import { NotFound } from "pages/NotFound";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { selectedAppSelector, setSelectedApp } from "redux/appSlice";
import { StoreDispatch } from "redux/store";

const AppLoadingIndicator = ({ name }: { name: String }) => {
    return (
        <div
            style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(0%, 0%)",
            }}
        >
            <Spin
                tip={`Loading ${name}...`}
                indicator={<LoadingOutlined style={{ fontSize: 96, color: Palette.HeroGreen }} />}
                style={{
                    color: Palette.HeroGreen,
                }}
            >
                <div />
            </Spin>
        </div>
    );
};

export const GlobalRouter = () => {
    const tmsApp = useApp(SupportedApps.tms);
    const pmgApp = useApp(SupportedApps.pmg);
    const dmsApp = useApp(SupportedApps.dms);
    const authApp = useApp(SupportedApps.authApp);

    const allConfiguredApps = getActiveApps();
    const dispatch = useDispatch<StoreDispatch>();
    const navigate = useNavigate();

    const location = useLocation();
    const selectedApp = useSelector(selectedAppSelector);

    const activeAppFromLocation = useMemo(() => {
        const firstUrlSegment = location.pathname.split("/")[1];

        if (!firstUrlSegment) {
            return DefaultApp;
        }

        const activeApp = AppRouteMap.get(firstUrlSegment);

        return activeApp ?? DefaultApp;
    }, [location.pathname]);

    const applyAppSelection = useCallback(
        (target?: SupportedApp) => {
            if (!target) {
                return;
            }

            dispatch(setSelectedApp(target));
            navigate(`/${target.name}`, { replace: true });
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        const target = allConfiguredApps.find((a) => a.name === activeAppFromLocation.name);

        if (target && target.name !== selectedApp?.name) {
            dispatch(setSelectedApp(target));
        }
    }, [activeAppFromLocation, allConfiguredApps, selectedApp, dispatch]);

    const switchApp = (targetApp: string) => {
        const target = allConfiguredApps.find((a) => a.name === targetApp);
        if (!target) {
            // eslint-disable-next-line no-console
            console.error(`Unconfigured app ${targetApp}`);
            return;
        }

        applyAppSelection(target);
    };

    const unauthenticatedHandler = useCallback(
        (_backUrl: string) => {
            navigate("/auth");
        },
        [navigate],
    );

    const { getUserName } = useAuth();
    const otherApps = allConfiguredApps.map((a) => a.name).filter((a) => a !== activeAppFromLocation.name);

    const getRouter = (app: SupportedApp) => {
        if (SupportedApps.tms.equals(app)) {
            if (!tmsApp.isLoaded()) return <Route path="*" element={<AppLoadingIndicator name="TMS" />} />;

            return SupportedApps.pmg.enabled ? (
                <Route
                    path="/TMS/*"
                    element={
                        <TmsPmgAppContainer
                            key="tms"
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            unauthenticatedHandler={unauthenticatedHandler}
                        />
                    }
                />
            ) : (
                <Route
                    path="/TMS/*"
                    element={
                        <TmsAppContainer
                            key="tms"
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            unauthenticatedHandler={unauthenticatedHandler}
                        />
                    }
                />
            );
        }

        if (SupportedApps.pmg.equals(app)) {
            if (!pmgApp.isLoaded()) return <Route path="*" element={<AppLoadingIndicator name="PMG" />} />;

            return (
                <Route
                    path="/PMG/*"
                    element={
                        <PmgAppContainer
                            key="pmg"
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            unauthenticatedHandler={unauthenticatedHandler}
                        />
                    }
                />
            );
        }

        if (SupportedApps.dms.equals(app)) {
            if (!dmsApp.isLoaded()) return <Route path="*" element={<AppLoadingIndicator name="DMS" />} />;

            return (
                <Route
                    path="/DMS/*"
                    element={
                        <DmsContainer
                            key="dms"
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            unauthenticatedHandler={unauthenticatedHandler}
                        />
                    }
                />
            );
        }

        if (SupportedApps.authApp.equals(app)) {
            if (!authApp.isLoaded()) return <Route path="*" element={<AppLoadingIndicator name="Auth App" />} />;

            return (
                <Route
                    path="/AuthApp/*"
                    element={
                        <AuthAppContainer
                            key="authapp"
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            unauthenticatedHandler={unauthenticatedHandler}
                        />
                    }
                />
            );
        }

        if (SupportedApps.dummy.equals(app))
            return (
                <Route
                    path="/Dummy/*"
                    element={
                        <DummyContainer
                            headerProps={{
                                appTitle: selectedApp.title,
                                userProfile: { apps: otherApps, switchApp },
                                username: getUserName(),
                            }}
                            note="dummy1"
                        />
                    }
                />
            );

        // eslint-disable-next-line no-console
        console.error("No router defined for app", app);
        return <>No router defined for app</>;
    };

    return (
        <Routes>
            {getRouter(activeAppFromLocation)}
            <Route path="/auth" element={<AuthPage />} />
            <Route
                path="/app-reset"
                element={
                    <AppReset
                        appTitle="Manual application selector"
                        userProfile={{ apps: allConfiguredApps.map((app) => app.name), switchApp }}
                        username={getUserName()}
                    />
                }
            />

            <Route path="/" element={<Navigate to={`/${DefaultApp.name}`} replace />} />

            <Route
                path="*"
                element={<NotFound appTitle={selectedApp.title} userProfile={{ apps: otherApps, switchApp }} username={getUserName()} />}
            />
        </Routes>
    );
};
